import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  InputAdornment,
  TextField,
  FormControlLabel,
  Checkbox,
  Button
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { apiService } from '../services/api';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const PricingAnalysis = () => {
  const [pricingData, setPricingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [showOnlyWithCost, setShowOnlyWithCost] = useState(false);
  const [showOnlyWithSales, setShowOnlyWithSales] = useState(false);
  const [summary, setSummary] = useState(null);
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(30, 'day'));
  const [dateTo, setDateTo] = useState(dayjs());
  const [showAllTime, setShowAllTime] = useState(false);
  const [sortField, setSortField] = useState('totalProfit');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    loadPricingData();
  }, []);

  // Reload data when switching between all time and period selection
  useEffect(() => {
    if (showAllTime) {
      loadPricingData();
    }
  }, [showAllTime]);

  const loadPricingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      
      // Add date filtering if not showing all time
      if (!showAllTime) {
        params.date_from = dateFrom.format('YYYY-MM-DD');
        params.date_to = dateTo.format('YYYY-MM-DD');
        console.log('💰 Loading pricing analysis for period:', params.date_from, 'to', params.date_to);
      } else {
        console.log('💰 Loading pricing analysis for all time...');
      }

      const response = await apiService.getPricingAnalysis(params);
      
      setPricingData(response.data || []);
      setSummary(response.summary || null);
      
      console.log('✅ Pricing analysis loaded:', response.data?.length, 'products');

    } catch (err) {
      console.error('❌ Pricing data loading error:', err);
      setError(`Fout bij laden van prijsgegevens: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpwardIcon fontSize="small" sx={{ opacity: 0.3, ml: 0.5 }} />;
    }
    return sortDirection === 'desc' ? 
      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />;
  };

  const formatCurrency = (amount) => {
    if (amount === 0) return '€0,00';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (percentage) => {
    if (percentage === 0) return '0%';
    if (percentage === null || percentage === undefined || isNaN(percentage)) return 'N/A';
    return `${percentage.toFixed(1)}%`;
  };

  const getMarginColor = (margin) => {
    if (margin === 0) return 'default';
    if (margin > 50) return 'success';
    if (margin > 25) return 'warning';
    if (margin > 0) return 'info';
    return 'error';
  };

  const getMarginIcon = (margin) => {
    if (margin === 0) return null;
    if (margin > 0) return <TrendingUpIcon fontSize="small" />;
    return <TrendingDownIcon fontSize="small" />;
  };

  // Filter and sort products based on search, checkboxes, and sort criteria
  const filteredProducts = pricingData
    .filter(product => {
      const matchesSearch = product.productName.toLowerCase().includes(searchFilter.toLowerCase()) ||
                           (product.sku && product.sku.toLowerCase().includes(searchFilter.toLowerCase()));
      
      const matchesCostFilter = !showOnlyWithCost || product.purchasePrice > 0;
      const matchesSalesFilter = !showOnlyWithSales || product.quantitySold > 0;
      
      return matchesSearch && matchesCostFilter && matchesSalesFilter;
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      
      let aValue, bValue;
      
      switch (sortField) {
        case 'profitMargin':
          aValue = a.profitMargin || 0;
          bValue = b.profitMargin || 0;
          break;
        case 'quantitySold':
          aValue = a.quantitySold || 0;
          bValue = b.quantitySold || 0;
          break;
        case 'totalRevenue':
          aValue = a.totalRevenue || 0;
          bValue = b.totalRevenue || 0;
          break;
        case 'totalProfit':
          aValue = a.totalProfit || 0;
          bValue = b.totalProfit || 0;
          break;
        case 'orderCount':
          aValue = a.orderCount || 0;
          bValue = b.orderCount || 0;
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Prijsanalyse wordt geladen...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        <AttachMoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Prijsanalyse per SKU
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" paragraph>
        Inkoopprijs, verkoopprijs en winstmarge per product. 
        {showAllTime 
          ? ' Toont alle historische data. Tabel gesorteerd op totale winst.' 
          : ` Periode: ${dateFrom.format('DD-MM-YYYY')} tot ${dateTo.format('DD-MM-YYYY')}.`
        }
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {summary.totalProducts}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Totaal Producten
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {summary.productsWithCost}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Met Inkoopprijs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {summary.productsWithSales}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Met Verkopen
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {formatPercentage(summary.averageMargin)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Gemiddelde Marge
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Additional Summary Row */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(summary.totalRevenue)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Totale Omzet
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color={summary.totalProfit >= 0 ? "success.main" : "error.main"}>
                  {formatCurrency(summary.totalProfit)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Totale Winst
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {summary.totalRevenue > 0 ? formatPercentage((summary.totalProfit / summary.totalRevenue) * 100) : '0%'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Winst Percentage
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Period Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Periode Selectie
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAllTime}
                  onChange={(e) => setShowAllTime(e.target.checked)}
                  color="primary"
                />
              }
              label="Alle tijd - toon complete historische data (standaard)"
            />
            <Typography variant="caption" color="textSecondary" sx={{ ml: 4, display: 'block' }}>
              Uitvinken om een specifieke periode te selecteren
            </Typography>
          </Grid>
        </Grid>
        
        {!showAllTime && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Van datum"
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue)}
                format="DD-MM-YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Tot datum"
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue)}
                format="DD-MM-YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={loadPricingData}
                disabled={loading}
                fullWidth
                sx={{ height: '56px' }}
              >
                Analyse Uitvoeren
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Filter Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Zoek op SKU of productnaam"
              variant="outlined"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyWithCost}
                  onChange={(e) => setShowOnlyWithCost(e.target.checked)}
                  color="primary"
                />
              }
              label="Alleen met inkoopprijs"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyWithSales}
                  onChange={(e) => setShowOnlyWithSales(e.target.checked)}
                  color="primary"
                />
              }
              label="Alleen met verkopen"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={loadPricingData}
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Vernieuwen
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Prijsoverzicht ({filteredProducts.length} producten)
            {sortField && (
              <Typography variant="body2" color="textSecondary" component="span" sx={{ ml: 1 }}>
                - gesorteerd op {
                  sortField === 'profitMargin' ? 'marge' :
                  sortField === 'quantitySold' ? 'verkocht' :
                  sortField === 'totalRevenue' ? 'omzet' :
                  sortField === 'totalProfit' ? 'totale winst' :
                  sortField === 'orderCount' ? 'orders' : sortField
                } ({sortDirection === 'desc' ? 'hoog naar laag' : 'laag naar hoog'})
              </Typography>
            )}
          </Typography>
          {sortField && (
            <Button 
              size="small" 
              onClick={() => {setSortField(''); setSortDirection('desc');}}
              variant="outlined"
            >
              Reset Sortering
            </Button>
          )}
        </Box>
        
        {filteredProducts.length === 0 ? (
          <Alert severity="info">
            Geen producten gevonden die voldoen aan de filtercriteria.
          </Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>SKU</strong></TableCell>
                <TableCell><strong>Productnaam</strong></TableCell>
                <TableCell align="right"><strong>Inkoopprijs</strong></TableCell>
                <TableCell align="right"><strong>Verkoopprijs</strong></TableCell>
                <TableCell 
                  align="right" 
                  sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleSort('profitMargin')}
                >
                  <Tooltip title="Klik om te sorteren op winstmarge">
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <strong>Marge</strong>
                      {getSortIcon('profitMargin')}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleSort('quantitySold')}
                >
                  <Tooltip title="Klik om te sorteren op verkochte hoeveelheid">
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <strong>Verkocht</strong>
                      {getSortIcon('quantitySold')}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleSort('totalRevenue')}
                >
                  <Tooltip title="Klik om te sorteren op totale omzet">
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <strong>Omzet</strong>
                      {getSortIcon('totalRevenue')}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleSort('totalProfit')}
                >
                  <Tooltip title="Klik om te sorteren op totale winst (standaard)">
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <strong>Totale Winst</strong>
                      {getSortIcon('totalProfit')}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleSort('orderCount')}
                >
                  <Tooltip title="Klik om te sorteren op aantal orders">
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <strong>Orders</strong>
                      {getSortIcon('orderCount')}
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={`${product.sku}-${product.productName}`}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {product.sku}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {product.productName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={product.purchasePrice > 0 ? 'text.primary' : 'text.secondary'}>
                      {product.purchasePrice > 0 ? formatCurrency(product.purchasePrice) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={product.sellingPrice > 0 ? 'text.primary' : 'text.secondary'}>
                      {product.sellingPrice > 0 ? formatCurrency(product.sellingPrice) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {product.profitMargin > 0 ? (
                      <Chip
                        icon={getMarginIcon(product.profitMargin)}
                        label={formatPercentage(product.profitMargin)}
                        color={getMarginColor(product.profitMargin)}
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={product.quantitySold > 0 ? 'text.primary' : 'text.secondary'}>
                      {product.quantitySold > 0 ? product.quantitySold : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={product.totalRevenue > 0 ? 'text.primary' : 'text.secondary'}>
                      {product.totalRevenue > 0 ? formatCurrency(product.totalRevenue) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      color={product.totalProfit === 0 ? 'text.secondary' : product.totalProfit > 0 ? 'success.main' : 'error.main'}
                      fontWeight={product.totalProfit !== 0 ? 'bold' : 'normal'}
                    >
                      {product.totalProfit !== 0 ? formatCurrency(product.totalProfit) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={product.orderCount > 0 ? 'text.primary' : 'text.secondary'}>
                      {product.orderCount > 0 ? product.orderCount : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default PricingAnalysis; 